import { buildConfigId, countClass, loadStudentCourseFolderCatalog, loadStudentCourseFolderIndex, loadStudentCoursePreview, pointer, pointerId, queryAllClass, queryClass, runCloud, type ParseRecord } from './parse';
import { ADMIN_CLASSES, queryAdmin } from './adminApi';
import { loadCourseProgressSummaries } from './certificates';
import type { CourseProgress } from './certificates';
import type { Course, CourseFolder, DashboardStats, StudentCourse, TestInfo } from './types';

function displayCourse(course: Course, lectureTotals: Map<string, number>, progressByCourse: Map<string, CourseProgress>): StudentCourse {
  const progress = progressByCourse.get(course.objectId);
  // Real watch percent; courses never started simply sit at 0%.
  const percent = progress?.percent ?? 0;
  return {
    ...course,
    progress: percent,
    faculty: course.CourseSubTitle ?? '',
    lessonCount: progress?.totalLectures ?? lectureTotals.get(course.objectId) ?? 0,
    lastStudied: percent === 0 ? 'Not started yet' : progress?.complete ? 'All lectures done' : 'In progress',
    completed: Boolean(progress?.complete),
    kind: course.CourseTitle?.toLowerCase().includes('test') || course.CourseTitle?.toLowerCase().includes('mock') ? 'test' : course.CoursePaidType === 'Free' ? 'free' : 'course',
    lastAccessedAt: progress?.lastAccessedAt,
  };
}

export async function loadStudentCourses(): Promise<StudentCourse[]> {
  try {
    const courses = (await queryAllClass('elearning_Course', { order: 'createdAt' }) as Course[])
      .filter((course) => !course.IsDeleted);
    if (!courses.length) return [];
    const [summaries, lectureTotals] = await Promise.all([
      loadCourseProgressSummaries().catch(() => [] as CourseProgress[]),
      // Lecture totals for courses without any watched lecture yet.
      Promise.all(courses.map((course) =>
        countClass('elearning_Videos', { where: (query) => query.equalTo('CoursePtr', pointer('elearning_Course', course.objectId)) }).catch(() => 0),
      )),
    ]);
    const progressByCourse = new Map(summaries.map((summary) => [summary.courseId, summary]));
    const totals = new Map(courses.map((course, index) => [course.objectId, lectureTotals[index]]));
    return courses.map((course) => displayCourse(course, totals, progressByCourse));
  } catch (error) {
    console.warn('AyurGuide course query failed', error);
  }
  return [];
}

export async function loadStudentCourseFolders(): Promise<CourseFolder[]> {
  const { results } = await loadStudentCourseFolderIndex();
  return (results as CourseFolder[]).sort((left, right) =>
    String(left.FolderName ?? '').localeCompare(String(right.FolderName ?? ''), undefined, { sensitivity: 'base' }),
  );
}

/** Load only one learner-facing folder and its assigned course cards. */
export async function loadStudentCourseFolderContents(folderId: string): Promise<{ folder: CourseFolder | null; courses: StudentCourse[] }> {
  const { folder: selectedFolder, courses: courseRows } = await loadStudentCourseFolderCatalog(folderId);
  if (!selectedFolder) return { folder: null, courses: [] };
  const folder = selectedFolder as CourseFolder;
  const assigned = courseRows as Course[];
  return {
    folder,
    // Folder discovery should not wait for dashboard progress rollups. The
    // course detail page loads the learner's current progress when opened.
    courses: assigned.map((course) => displayCourse(course, new Map(), new Map())),
  };
}

function displayOrder(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function sortDisplayOrder<T extends ParseRecord & { DisplayOrder?: number | string }>(records: T[]) {
  return [...records].sort((left, right) => {
    const leftOrder = displayOrder(left.DisplayOrder);
    const rightOrder = displayOrder(right.DisplayOrder);
    if (leftOrder !== undefined && rightOrder !== undefined && leftOrder !== rightOrder) return leftOrder - rightOrder;
    if (leftOrder !== undefined && rightOrder === undefined) return -1;
    if (leftOrder === undefined && rightOrder !== undefined) return 1;
    const dateOrder = String(left.createdAt ?? '').localeCompare(String(right.createdAt ?? ''));
    return dateOrder || left.objectId.localeCompare(right.objectId);
  });
}

type CourseVideoRecord = ParseRecord & {
  DisplayOrder?: number | string;
  ParentFolder?: unknown;
  SubjectsPtr?: unknown;
  Type?: string;
  VideoName?: string;
};

function courseVideoDisplayOrder(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Sort syllabus entries by an explicit curriculum order, then naturally by name. */
function sortCourseVideoItems(items: CourseVideoRecord[]) {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return [...items].sort((left, right) => {
    const leftOrder = courseVideoDisplayOrder(left.DisplayOrder);
    const rightOrder = courseVideoDisplayOrder(right.DisplayOrder);
    if (leftOrder !== undefined && rightOrder !== undefined && leftOrder !== rightOrder) return leftOrder - rightOrder;
    if (leftOrder !== undefined && rightOrder === undefined) return -1;
    if (leftOrder === undefined && rightOrder !== undefined) return 1;
    const leftIsFolder = left.Type === 'Folder';
    const rightIsFolder = right.Type === 'Folder';
    if (leftIsFolder !== rightIsFolder) return leftIsFolder ? -1 : 1;
    return collator.compare(left.VideoName || '', right.VideoName || '') || left.objectId.localeCompare(right.objectId);
  });
}

/** Load the immediate folders and videos for one course subject/folder location. */
export async function loadCourseVideoDirectory(courseId: string, subjectId?: string, parentFolderId?: string) {
  const items = await queryAllClass('elearning_Videos', {
    order: 'createdAt',
    where: (query) => {
      query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
      query.equalTo('SubjectsPtr', subjectId ? pointer('elearning_Subjects', subjectId) : null);
      query.equalTo('ParentFolder', parentFolderId ? pointer('elearning_Videos', parentFolderId) : null);
      query.containedIn('Type', ['Folder', 'Original']);
      query.notEqualTo('IsDeleted', true);
    },
  });
  return sortCourseVideoItems(items as CourseVideoRecord[]);
}

/** Resolve and validate a folder's breadcrumb chain within its course and subject. */
export async function loadCourseVideoFolderTrail(courseId: string, subjectId?: string, folderId?: string) {
  const trail: Array<{ objectId: string; title: string }> = [];
  const visited = new Set<string>();
  let currentId = folderId;
  while (currentId) {
    if (visited.has(currentId)) throw new Error('This syllabus folder hierarchy contains a cycle.');
    visited.add(currentId);
    const [folder] = await queryClass('elearning_Videos', {
      limit: 1,
      keys: ['VideoName', 'Type', 'CoursePtr', 'SubjectsPtr', 'ParentFolder', 'IsDeleted'],
      where: (query) => {
        query.equalTo('objectId', currentId);
        query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
        query.equalTo('SubjectsPtr', subjectId ? pointer('elearning_Subjects', subjectId) : null);
        query.equalTo('Type', 'Folder');
        query.notEqualTo('IsDeleted', true);
      },
    });
    if (!folder) throw new Error('This syllabus folder is no longer available.');
    trail.unshift({ objectId: folder.objectId, title: folder.VideoName || 'Untitled folder' });
    currentId = pointerId(folder.ParentFolder);
  }
  return trail;
}

export async function loadCourseTestFolderItems(courseId: string, parentFolderId?: string) {
  const baseWhere: WhereFn = (query) => {
    query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
    query.equalTo('ParentFolder', parentFolderId ? pointer('test_TestInfo', parentFolderId) : null);
    query.notEqualTo('IsDeleted', true);
  };
  const [folders, tests] = await Promise.all([
    queryAllClass('test_TestInfo', { order: 'DisplayOrder,createdAt', where: (query) => { baseWhere(query); query.equalTo('Type', 'Folder'); } }) as Promise<TestInfo[]>,
    queryAllClass('test_TestInfo', { order: 'createdAt', where: (query) => { baseWhere(query); query.notEqualTo('Type', 'Folder'); query.equalTo('IsPublished', true); } }) as Promise<TestInfo[]>,
  ]);
  return { folders: sortDisplayOrder(folders), tests: sortDisplayOrder(tests) };
}

export async function loadCourseTestFolderTrail(courseId: string, folderId?: string) {
  const trail: Array<{ objectId: string; title: string }> = [];
  const visited = new Set<string>();
  let currentId = folderId;
  while (currentId && trail.length < 16) {
    if (visited.has(currentId)) throw new Error('This test folder hierarchy contains a cycle.');
    visited.add(currentId);
    const [folder] = await queryClass('test_TestInfo', {
      limit: 1,
      keys: ['TestTitle', 'Type', 'CoursePtr', 'ParentFolder', 'IsDeleted'],
      where: (query) => {
        query.equalTo('objectId', currentId);
        query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
        query.equalTo('Type', 'Folder');
        query.notEqualTo('IsDeleted', true);
      },
    }) as TestInfo[];
    if (!folder) throw new Error('This test folder is no longer available.');
    trail.unshift({ objectId: folder.objectId, title: folder.TestTitle || 'Untitled folder' });
    currentId = pointerId(folder.ParentFolder);
  }
  if (currentId) throw new Error('This test folder hierarchy is deeper than the app supports.');
  return trail;
}

type WhereFn = NonNullable<Parameters<typeof queryClass>[1]>['where'];

function escapeRegex(input: string) {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type TestTab = 'all' | 'practice' | 'exam';

function testListWhere(search: string, tab: TestTab): WhereFn {
  return (query) => {
    query.equalTo('IsPublished', true);
    query.notEqualTo('Type', 'Folder');
    if (tab === 'practice') query.equalTo('PracticeMode', true);
    if (tab === 'exam') query.equalTo('EvalutionMode', true);
    if (search) query.matches('TestTitle', new RegExp(escapeRegex(search), 'i'));
  };
}

/** One page of the published-test catalog plus the exact filtered total. */
export async function loadTestsPage(options: { page?: number; pageSize?: number; search?: string; tab?: TestTab } = {}): Promise<{ items: TestInfo[]; total: number }> {
  const { page = 0, pageSize = 15, search = '', tab = 'all' } = options;
  const where = testListWhere(search.trim(), tab);
  const [items, total] = await Promise.all([
    queryClass('test_TestInfo', { limit: pageSize, skip: page * pageSize, order: '-createdAt', where }) as Promise<TestInfo[]>,
    countClass('test_TestInfo', { where }),
  ]);
  return { items, total };
}

export async function loadTests(): Promise<TestInfo[]> {
  try {
    const { items } = await loadTestsPage({ page: 0, pageSize: 100 });
    return items;
  } catch (error) {
    console.warn('AyurGuide test query failed', error);
  }
  return [];
}

export async function loadDashboardStats(): Promise<DashboardStats> {
  const names = ['elearning_Course', 'elearning_Customer', 'elearning_Subscriptions', 'test_TestInfo', 'elearning_Videos', 'test_TestQuestions'] as const;
  const [courses, students, subscriptions, tests, videos, questions] = await Promise.all(names.map((name) => countClass(name).catch(() => 0)));
  return { courses, students, subscriptions, tests, videos, questions };
}

export type AdminDashboardData = {
  paidUsers: number;
  freeUsers: number;
  totalSale: number;
  subscriptions: number;
  courses: number;
  saleSource: 'production-aggregate' | 'rest-fallback';
  usersSeries: Array<{ label: string; value: number; tone: 'paid' | 'free' }>;
  salesSeries: Array<{ label: string; value: number }>;
};

export type DashboardRange = 'All' | 'Today' | 'Yesterday' | 'This week' | 'Last 7 days' | 'Last week' | 'Last 14 days' | 'This month' | 'Last month' | 'Last 30 days' | 'This year' | 'Last year';

function dashboardRangeBounds(range: DashboardRange) {
  const today = new Date();
  const utcStart = (year: number, month: number, day: number) => new Date(Date.UTC(year, month, day, 0, 0, 0)).toISOString();
  const utcEnd = (year: number, month: number, day: number) => new Date(Date.UTC(year, month, day, 23, 59, 59)).toISOString();
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const sunday = new Date(day);
  sunday.setDate(day.getDate() - day.getDay());
  const previousSunday = new Date(sunday);
  previousSunday.setDate(sunday.getDate() - 7);
  switch (range) {
    case 'Today': return { from: utcStart(today.getFullYear(), today.getMonth(), today.getDate()), to: utcEnd(today.getFullYear(), today.getMonth(), today.getDate()) };
    case 'Yesterday': { const value = new Date(day); value.setDate(value.getDate() - 1); return { from: utcStart(value.getFullYear(), value.getMonth(), value.getDate()), to: utcEnd(value.getFullYear(), value.getMonth(), value.getDate()) }; }
    case 'This week': return { from: utcStart(sunday.getFullYear(), sunday.getMonth(), sunday.getDate()), to: utcEnd(today.getFullYear(), today.getMonth(), today.getDate()) };
    case 'Last 7 days': { const value = new Date(day); value.setDate(value.getDate() - 7); return { from: utcStart(value.getFullYear(), value.getMonth(), value.getDate()), to: utcEnd(today.getFullYear(), today.getMonth(), today.getDate()) }; }
    case 'Last 14 days': { const value = new Date(day); value.setDate(value.getDate() - 14); return { from: utcStart(value.getFullYear(), value.getMonth(), value.getDate()), to: utcEnd(today.getFullYear(), today.getMonth(), today.getDate()) }; }
    case 'Last 30 days': { const value = new Date(day); value.setDate(value.getDate() - 30); return { from: utcStart(value.getFullYear(), value.getMonth(), value.getDate()), to: utcEnd(today.getFullYear(), today.getMonth(), today.getDate()) }; }
    case 'Last week': { const end = new Date(sunday); end.setDate(end.getDate() - 1); return { from: utcStart(previousSunday.getFullYear(), previousSunday.getMonth(), previousSunday.getDate()), to: utcEnd(end.getFullYear(), end.getMonth(), end.getDate()) }; }
    case 'This month': return { from: utcStart(today.getFullYear(), today.getMonth(), 1), to: utcEnd(today.getFullYear(), today.getMonth() + 1, 0) };
    case 'Last month': return { from: utcStart(today.getFullYear(), today.getMonth() - 1, 1), to: utcEnd(today.getFullYear(), today.getMonth(), 0) };
    case 'This year': return { from: utcStart(today.getFullYear(), 0, 1), to: utcEnd(today.getFullYear(), 11, 31) };
    case 'Last year': return { from: utcStart(today.getFullYear() - 1, 0, 1), to: utcEnd(today.getFullYear() - 1, 11, 31) };
    default: return undefined;
  }
}

function matchDateField(field: string, range: DashboardRange) {
  const bounds = dashboardRangeBounds(range);
  if (!bounds) return '';
  return `,'${field}':{'$gte':'${bounds.from}'${bounds.to ? `,'$lte':'${bounds.to}'` : ''}}`;
}

async function dashboardAggregate(condition: string, className: string) {
  const sessionToken = sessionStorage.getItem('ayurguide-v3-session');
  if (!sessionToken) return null;
  try {
    const result = await runCloud<unknown>('UniqueQuery', { condition, className }, { headers: { sessiontoken: sessionToken } });
    return Array.isArray(result) ? result as Array<Record<string, any>> : null;
  } catch {
    return null;
  }
}

function dashboardUserCondition(range: DashboardRange) {
  return `match={'AppId':'${buildConfigId}'${matchDateField('updatedAt', range)}}&group={'objectId':'$IsPaid','Total':{'$sum':1},'IsPaid':{'$first':'$IsPaid'}}&project={'name':{'$cond':[{'$eq':['$IsPaid',true]},'Paid','Free']},'Total':1,'IsHepaid':1}`;
}

function dashboardSaleCondition(range: DashboardRange) {
  const bounds = dashboardRangeBounds(range);
  const date = bounds ? `{'$exists':true,'$gte':'${bounds.from}'${bounds.to ? `,'$lte':'${bounds.to}'` : ''}}` : "{'$exists':true}";
  return `match={'AppId':'${buildConfigId}','TxnStatus':'Success','TxnDate':${date}}&group={'objectId':null,'Total':{'$sum':'$Price'}}&project={'objectId':1,'Total':1}`;
}

function dashboardSalesSeriesCondition(range: DashboardRange) {
  const bounds = dashboardRangeBounds(range);
  const date = bounds ? `,'$gte':'${bounds.from}'${bounds.to ? `,'$lte':'${bounds.to}'` : ''}` : '';
  return `match={'AppId':'${buildConfigId}','TxnStatus':'Success','TxnDate':{'$exists':true${date}}}&group={'objectId':{'$concat':[{'$substr':[{'$dayOfMonth':'$TxnDate'},0,4]},'/',{'$substr':[{'$month':'$TxnDate'},0,4]},'/',{'$substr':[{'$year':'$TxnDate'},0,4]}]},'Total':{'$sum':'$Price'}}&sort={'TxnDate':1}&project={'objectId':1,'Total':1}`;
}

function dashboardSeriesDate(label: string) {
  const parts = label.split('/').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part)) || parts[2] < 1000) return 0;
  return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
}

export async function loadAdminDashboardData(range: DashboardRange = 'All'): Promise<AdminDashboardData> {
  const [userAggregate, saleAggregate, salesAggregate, subscriptions, courses] = await Promise.all([
    dashboardAggregate(dashboardUserCondition(range), ADMIN_CLASSES.customer),
    dashboardAggregate(dashboardSaleCondition(range), ADMIN_CLASSES.subscription),
    dashboardAggregate(dashboardSalesSeriesCondition(range), ADMIN_CLASSES.subscription),
    countClass(ADMIN_CLASSES.subscription),
    countClass(ADMIN_CLASSES.course),
  ]);
  const paidUsers = Number(userAggregate?.find((row) => row.name === 'Paid')?.Total ?? 0);
  const freeUsers = Number(userAggregate?.find((row) => row.name === 'Free')?.Total ?? 0);
  const exactSale = saleAggregate?.[0]?.Total;
  const hasBoundedAggregate = range !== 'All' && userAggregate !== null && saleAggregate !== null;
  if (hasBoundedAggregate || (userAggregate && exactSale !== undefined)) {
    // The legacy aggregate endpoint can return many date buckets and does not
    // consistently honor a post-group sort. Keep the dashboard readable while
    // preserving the newest valid buckets and full card totals.
    const salesSeries = (salesAggregate ?? []).map((row, index) => ({ label: String(row.objectId ?? `T${index + 1}`), value: Number(row.Total ?? 0) })).sort((left, right) => dashboardSeriesDate(left.label) - dashboardSeriesDate(right.label)).slice(-60);
    return { paidUsers, freeUsers, totalSale: Number(exactSale ?? 0), subscriptions, courses, saleSource: 'production-aggregate', usersSeries: [{ label: 'Paid', value: paidUsers, tone: 'paid' }, { label: 'Free', value: freeUsers, tone: 'free' }], salesSeries };
  }
  const subscriptionRows = await queryAdmin(ADMIN_CLASSES.subscription, ['Price', 'TxnStatus', 'TxnDate', 'createdAt'], { limit: 120, order: 'TxnDate', where: (query) => query.equalTo('TxnStatus', 'Success') }).catch(() => []);
  const successful = subscriptionRows.filter((row) => String(row.TxnStatus ?? '').toLowerCase() === 'success');
  const totalSale = successful.reduce((sum, row) => sum + Number(row.Price ?? 0), 0);
  const salesSeries = subscriptionRows.slice(0, 12).map((row, index) => { const date = parseParseDate(row.TxnDate ?? row.createdAt); return { label: date ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : `T${index + 1}`, value: Number(row.Price ?? 0) }; });
  const fallbackPaid = paidUsers || await countClass(ADMIN_CLASSES.customer, { where: (query) => query.equalTo('IsPaid', true) });
  const fallbackFree = freeUsers || await countClass(ADMIN_CLASSES.customer, { where: (query) => query.equalTo('IsPaid', false) });
  return { paidUsers: fallbackPaid, freeUsers: fallbackFree, totalSale, subscriptions, courses, saleSource: 'rest-fallback', usersSeries: [{ label: 'Paid', value: fallbackPaid, tone: 'paid' }, { label: 'Free', value: fallbackFree, tone: 'free' }], salesSeries };
}

function parseParseDate(value: unknown) {
  if (value && typeof value === 'object' && 'iso' in value) return new Date(String((value as { iso: string }).iso));
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export async function loadCourseBundle(courseId: string) {
  const preview = await loadStudentCoursePreview(courseId);
  const course = preview.course as Course;
  if (!preview.contentAllowed) return {
    course,
    subjects: [],
    materials: [],
    tests: [],
    lectureCount: 0,
    materialCount: 0,
    testCount: 0,
    unassignedRootCount: 0,
    contentAllowed: false,
  };
  const coursePointer = pointer('elearning_Course', courseId);
  const [subjects, materials, tests, lectureCount, materialCount, testCount, unassignedRootCount] = await Promise.all([
    queryAllClass('elearning_Subjects', { order: 'DisplayOrder,createdAt', where: (q) => { q.equalTo('CoursePtr', coursePointer); q.notEqualTo('IsDeleted', true); } }).then(sortDisplayOrder),
    queryAllClass('elearning_CourseMaterial', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.notEqualTo('IsDeleted', true); } }),
    queryClass('test_TestInfo', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.equalTo('IsPublished', true); q.notEqualTo('Type', 'Folder'); }, limit: 100 }),
    countClass('elearning_Videos', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.equalTo('Type', 'Original'); q.notEqualTo('IsDeleted', true); } }),
    countClass('elearning_CourseMaterial', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.notEqualTo('IsDeleted', true); } }),
    countClass('test_TestInfo', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.equalTo('IsPublished', true); q.notEqualTo('Type', 'Folder'); } }),
    countClass('elearning_Videos', { where: (q) => { q.equalTo('CoursePtr', coursePointer); q.equalTo('SubjectsPtr', null); q.equalTo('ParentFolder', null); q.containedIn('Type', ['Folder', 'Original']); q.notEqualTo('IsDeleted', true); } }),
  ]);
  return { course, subjects, materials, tests, lectureCount, materialCount, testCount, unassignedRootCount, contentAllowed: true };
}
